import { preparePhoto } from 'rentalui-photo-upload';
import { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';

// v9 customer_support_tickets - "Raise a ticket" from the homepage footer's Customer support
// section. Replaces the old owner/admin-only "Report Damage" button as the way anyone flags a
// problem. Posts to /api/support-tickets (public); photos upload first to
// /api/uploads/support-photo (also public, rate-limited).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const CATEGORIES = [
  { value: 'damage', label: 'Damage or condition' },
  { value: 'billing', label: 'Billing or payment' },
  { value: 'booking', label: 'A booking / order problem' },
  { value: 'account', label: 'Account or login' },
  { value: 'other', label: 'Something else' }
];
const empty = { category: 'damage', name: '', email: '', phone: '', orderRef: '', message: '' };

export default function RaiseTicketModal({ show, onHide }) {
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]); // uploaded URLs
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const close = () => {
    onHide();
    setTimeout(() => {
      setForm(empty); setPhotos([]); setErrors({}); setDone(false); setServerError('');
    }, 300);
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    setUploading(true);
    setServerError('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('photo', await preparePhoto(file, { maxDimension: 1024 }));
        const res = await axios.post(`${API_BASE_URL}/api/uploads/support-photo`, fd);
        const url = res.data?.url || res.data?.data?.url;
        if (url) setPhotos((p) => [...p, url]);
      }
    } catch (err) {
      setServerError(err.response?.data?.msg || err.message || 'A photo could not be uploaded. Try a smaller image.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Please tell us your name';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.message.trim()) next.message = 'Tell us what went wrong';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitting(true);
    setServerError('');
    try {
      await axios.post(`${API_BASE_URL}/api/support-tickets`, {
        category: form.category,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        orderRef: form.orderRef.trim(),
        message: form.message.trim(),
        photos
      });
      setDone(true);
    } catch (err) {
      setServerError(err.response?.data?.msg || 'Could not raise your ticket. Check the API is running, then try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Raise a ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          <div>
            <Alert variant="success" className="mb-3">
              Thanks — your ticket is in. Our support team will get back to you by email.
            </Alert>
            <Button variant="outline-primary" size="sm" onClick={() => { setDone(false); setForm(empty); setPhotos([]); }}>
              Raise another ticket
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {serverError && <Alert variant="danger">{serverError}</Alert>}
            <Form.Group className="mb-2" controlId="tkt-category">
              <Form.Label className="small mb-0">What&apos;s this about? *</Form.Label>
              <Form.Select size="sm" value={form.category} onChange={set('category')}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Form.Select>
            </Form.Group>
            <div className="row g-2">
              <div className="col-sm-6">
                <Form.Group className="mb-2" controlId="tkt-name">
                  <Form.Label className="small mb-0">Your name *</Form.Label>
                  <Form.Control size="sm" value={form.name} onChange={set('name')} isInvalid={!!errors.name} />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-sm-6">
                <Form.Group className="mb-2" controlId="tkt-email">
                  <Form.Label className="small mb-0">Email *</Form.Label>
                  <Form.Control size="sm" type="email" value={form.email} onChange={set('email')} isInvalid={!!errors.email} />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-sm-6">
                <Form.Group className="mb-2" controlId="tkt-phone">
                  <Form.Label className="small mb-0">Phone (optional)</Form.Label>
                  <Form.Control size="sm" value={form.phone} onChange={set('phone')} />
                </Form.Group>
              </div>
              <div className="col-sm-6">
                <Form.Group className="mb-2" controlId="tkt-order">
                  <Form.Label className="small mb-0">Order / booking id (optional)</Form.Label>
                  <Form.Control size="sm" placeholder="e.g. A1B2C3" value={form.orderRef} onChange={set('orderRef')} />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-2" controlId="tkt-message">
              <Form.Label className="small mb-0">Tell us what happened *</Form.Label>
              <Form.Control size="sm" as="textarea" rows={3} value={form.message} onChange={set('message')} isInvalid={!!errors.message} />
              <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="tkt-photos">
              <Form.Label className="small mb-0">Photos (optional, up to 6)</Form.Label>
              <Form.Control size="sm" type="file" accept="image/*" multiple disabled={uploading || photos.length >= 6} onChange={addPhotos} />
              {uploading && <div className="small text-muted mt-1"><Spinner as="span" size="sm" className="me-1" />Uploading…</div>}
              {photos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {photos.map((url, i) => (
                    <span key={url} className="position-relative">
                      <img src={url.startsWith('http') ? url : `${API_BASE_URL}${url}`} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6 }} />
                      <button type="button" className="btn btn-sm btn-light position-absolute top-0 end-0 p-0 px-1"
                        onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} aria-label="Remove photo">×</button>
                    </span>
                  ))}
                </div>
              )}
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button size="sm" variant="outline-secondary" onClick={close}>Cancel</Button>
              <Button size="sm" type="submit" disabled={submitting || uploading}>{submitting ? 'Sending…' : 'Raise ticket'}</Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
